/**
 * Google Antigravity AI client using OAuth + PKCE flow
 */

import type {
  AIClient,
  ChatCompletionRequest,
  ChatCompletionResponse,
  GenerateStoryRequest,
  GenerateStoryResponse,
  OAuthTokenResponse,
} from "@/lib/ai/types.ts";
import { PROVIDERS, REDIRECT_URI } from "@/lib/ai/config.ts";
import { generatePKCEPair, generateState } from "@/lib/auth/pkce.ts";

export class AntigravityClient implements AIClient {
  provider = "antigravity" as const;
  accessToken: string;

  constructor(accessToken: string) {
    this.accessToken = accessToken;
  }

  /**
   * Step 1: Start OAuth flow - returns authorization URL
   */
  static async startOAuthFlow(): Promise<{
    authUrl: string;
    verifier: string;
    state: string;
  }> {
    const { verifier, challenge } = await generatePKCEPair();
    const state = generateState();

    const params = new URLSearchParams({
      client_id: PROVIDERS.antigravity.clientId,
      redirect_uri: REDIRECT_URI,
      response_type: "code",
      scope: PROVIDERS.antigravity.scopes!.join(" "),
      state,
      code_challenge: challenge,
      code_challenge_method: "S256",
      access_type: "offline", // Request refresh token
      prompt: "consent", // Force consent to get refresh token
    });

    const authUrl = `${PROVIDERS.antigravity.authUrl}?${params.toString()}`;
    return { authUrl, verifier, state };
  }

  /**
   * Step 2: Exchange authorization code for access token
   */
  static async exchangeCodeForToken(
    code: string,
    verifier: string,
  ): Promise<OAuthTokenResponse> {
    const response = await fetch(`/api/auth/google/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        code,
        code_verifier: verifier,
        redirect_uri: REDIRECT_URI,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token exchange failed: ${response.status} - ${error}`);
    }

    return await response.json();
  }

  /**
   * Refresh access token using refresh token
   */
  static async refreshToken(
    refreshToken: string,
  ): Promise<OAuthTokenResponse> {
    const response = await fetch(`/api/auth/google/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token refresh failed: ${response.status} - ${error}`);
    }

    return await response.json();
  }

  /**
   * Send chat completion request to Antigravity API
   */
  async chatCompletion(
    request: ChatCompletionRequest,
  ): Promise<ChatCompletionResponse> {
    const response = await fetch(`/api/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        provider: "google",
        prompt: request.messages[request.messages.length - 1]?.content || "",
        model: request.model || PROVIDERS.antigravity.defaultModel,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(
        `Antigravity API error: ${response.status} - ${error}`,
      );
    }

    const data = await response.json();

    // Backend returns { answer, raw }, convert to standard format
    return {
      id: "antigravity-backend",
      object: "chat.completion",
      created: Date.now(),
      model: request.model || PROVIDERS.antigravity.defaultModel,
      choices: [
        {
          index: 0,
          message: {
            role: "assistant",
            content: data.answer || "",
          },
          finish_reason: "stop",
        },
      ],
    };
  }

  /**
   * Generate story elements
   */
  async generateStory(
    request: GenerateStoryRequest,
  ): Promise<GenerateStoryResponse> {
    const systemPrompt = `You are a creative storyteller for children aged ${
      request.context.ageRange || "6-12"
    }. Generate story elements in ${request.context.language}. Return valid JSON only.`;

    const userPrompt = this.buildStoryPrompt(request);

    const completion = await this.chatCompletion({
      model: PROVIDERS.antigravity.defaultModel,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      temperature: 0.9,
      max_tokens: 1000,
    });

    const content = completion.choices[0]?.message?.content || "{}";
    return JSON.parse(content);
  }

  /**
   * Test connection to Antigravity API
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.chatCompletion({
        model: PROVIDERS.antigravity.defaultModel,
        messages: [{ role: "user", content: "Hello" }],
        max_tokens: 5,
      });
      return true;
    } catch {
      return false;
    }
  }

  private buildStoryPrompt(request: GenerateStoryRequest): string {
    const { element } = request;
    if (element === "full_story") {
      return "Generate a complete short story with character, location, quest, and skills.";
    }
    return `Generate a ${element} for a children's story. Return JSON: { "elements": [{ "type": "${element}", "name": "...", "description": "...", "attributes": {...} }] }`;
  }
}
